/**
 * PopoutShell — root component for popped-out windows.
 *
 * Renders just one workspace tab (the `kind` from URL params) for the
 * `projectId` from URL params. The popout is pinned to the project it was
 * opened against (live "follow the main window" rode a Tauri event the CEF
 * host doesn't emit — see the note in the body).
 *
 * No sidebar, no title bar UI of our own (the host window decorations cover
 * that). Just a tight wrapper that loads the project and renders the tab.
 */
import { useEffect, useState } from 'react';
import BrowserTab from '@/components/workspace/BrowserTab';
import TerminalTab from '@/components/workspace/TerminalTab';
import FilesTab from '@/components/workspace/FilesTab';
import { useProjectStore } from '@/stores/projectStore';
import { getPopoutContext, type PopoutKind } from '@/lib/popoutContext';
import type { Project } from '@/types/tauri';

export default function PopoutShell() {
  const ctx = getPopoutContext();
  const getProject = useProjectStore((s) => s.getProject);
  const liveCurrentProject = useProjectStore((s) => s.currentProject);

  const activeProjectId = ctx.projectId ?? '';
  const [project, setProject] = useState<Project | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Load project on id change.
  useEffect(() => {
    if (!activeProjectId) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getProject(activeProjectId);
        if (cancelled) return;
        if (!p) {
          setLoadError(`Project ${activeProjectId} not found`);
          return;
        }
        setProject(p);
        // Set in store too so child components that read currentProject
        // (BrowserTab uses it for settings + persistence) see the right
        // record. Don't trigger event broadcast — that's main-window-only.
        useProjectStore.setState({ currentProject: p });
        setLoadError(null);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [activeProjectId, getProject]);

  // NOTE: "follow the main window's active project" rode the Tauri
  // `active-project-changed` event, which the CEF host doesn't emit — so the
  // popout stays pinned to the project it was opened against (from the URL).
  // Re-introducing live follow under CEF awaits a host/core broadcast channel.

  // Keep our local mirror of currentProject in sync if the store updates
  // (e.g. user edits project settings — the store gets the new record).
  useEffect(() => {
    if (liveCurrentProject?.id === activeProjectId) {
      setProject(liveCurrentProject);
    }
  }, [liveCurrentProject, activeProjectId]);

  if (loadError) {
    return (
      <div className="flex h-screen items-center justify-center bg-base-100 text-base-content">
        <div className="alert alert-error max-w-md">
          <span>{loadError}</span>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex h-screen items-center justify-center bg-base-100 text-base-content/60">
        <span className="loading loading-spinner loading-md" />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-base-100 text-base-content overflow-hidden">
      <PopoutBody
        kind={ctx.kind}
        projectId={project.id}
        projectPath={project.root_path ?? undefined}
        agentType={project.agent_type ?? undefined}
      />
    </div>
  );
}

function PopoutBody({
  kind,
  projectId,
  projectPath,
  agentType,
}: {
  kind: PopoutKind | null;
  projectId: string;
  projectPath?: string;
  agentType?: string;
}) {
  if (kind === 'browser') {
    return <BrowserTab projectId={projectId} projectPath={projectPath} />;
  }
  if (kind === 'terminal') {
    return <TerminalTab projectPath={projectPath} agentType={agentType} />;
  }
  if (kind === 'files') {
    return <FilesTab projectId={projectId} />;
  }
  return (
    <div className="flex flex-1 items-center justify-center text-base-content/60 text-sm">
      Popout kind &ldquo;{kind ?? 'unknown'}&rdquo; not implemented yet.
    </div>
  );
}
